import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';

import httpInstances from '@/api/axiosInstance';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { GX_API_URL } from '@/constants/env';
import type { SymbolKey } from '@/entities/symbol';
import { pastExportsOptions } from '@/features/DataHub/api';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import {
  defaultExportSettings,
  ExcelExportStatus,
  ExportRequestOverrides,
  ExportRequestParams,
  ExportSettingsType,
  ExportTypes
} from '@/features/DataHub/modules/Export/types';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';
import { useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import copyToClipboard from '@/utils/copyToClipboard';
import downloadFile from '@/utils/downloadFile';

import { useLatestApiKey } from './useLatestApiKey';
import { exportRequestService } from '../services';
import { getFromToValues, scrambleSecrets, transformSymbols } from '../utils';

const EXPORT_ENDPOINT = 'export';
const DEFAULT_GROUP = 'Prod_Indexes';

const EXCEL_EXPORT_TYPE = 'excel';

const extractParamsFromUrl = (url: string) => {
  const parsed = new URL(url);
  const pathParts = parsed.pathname.split('/').filter(Boolean);
  const fileName = pathParts[1];
  const fileId = pathParts[2];
  return { fileName, fileId };
};

type ExportResponse = {
  downloadUrl?: string;
  message?: string;
};

interface BuildRequestBodyParams {
  id?: string;
  name: string | null;
  exportType: string;
  exportSubType: string | null;
  from?: string | null;
  to?: string | null;
  timezone?: string;
  dateFormat?: string;
  includeMetadata?: boolean;
  symbols?: SymbolKey[];
  group?: string;
}

function buildExportRequestBody(params: BuildRequestBodyParams): ExportRequestParams {
  const {
    id,
    name,
    exportType,
    exportSubType,
    from,
    to,
    timezone,
    dateFormat,
    includeMetadata,
    symbols,
    group
  } = params;

  const { symbols: transformedSymbols } = transformSymbols(symbols ?? [], group ?? 'Prod_Indexes');

  return {
    ...(id && { id }),
    name,
    exportType,
    exportSubType,
    from: from ?? null,
    to: to ?? null,
    timezone,
    dateFormat,
    includeMetadata,
    symbols: transformedSymbols
  };
}

const useDataHubExport = () => {
  // State

  const exportSliceType = useAppSelector(state => state.export.type);
  console.log(exportSliceType);
  const [exportSettings, setExportSettings] = useState<ExportSettingsType>({
    ...defaultExportSettings,
    exportType:
      exportSliceType && exportSliceType === ExportTypes.DATA ? ExportTypes.DATA : ExportTypes.IMAGE
  });
  const [previewOptionId, setPreviewOptionId] = useState<string | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [exportName, setExportName] = useState<string | null>(null);

  // Excel export modal state
  const [excelExportStatus, setExcelExportStatus] = useState<ExcelExportStatus>('idle');

  // Preview loading state (set by preview components)
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);

  // Preview download function (set by preview components for client-side downloads)
  const [previewDownloadFn, setPreviewDownloadFn] = useState<(() => void) | null>(null);

  // Content to copy when user clicks "Copy to clipboard" (set by preview components; e.g. Python script, Power Query, curl)
  const [previewContentToCopy, setPreviewContentToCopy] = useState<string | null>(null);

  // Close drawer with confirmation (registered by ExportDrawer)
  const [closeHandler, setCloseHandler] = useState<(() => void) | null>(null);
  const requestClose = useCallback(() => {
    closeHandler?.();
  }, [closeHandler]);
  const setRequestClose = useCallback((fn: () => void) => {
    setCloseHandler(() => fn);
  }, []);

  // Hooks & Selectors
  const { formatDateString } = useDateText();
  const { data: pastExports = [] } = useQuery(pastExportsOptions.list());
  const postHog = usePostHog();
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();

  const symbols = useAppSelector(state => state.symbolSelector.symbols);

  const { userConfig } = useAppSelector(state => state.userConfig);

  const userTimezone = userConfig.timezone.value;
  const userDateFormat = userConfig.dateFormat.value;

  // Export Name Generation
  const createExportName = useCallback((): string => {
    const formattedDate = formatDateString(new Date().toISOString(), DateTextVariants.FULL);
    let newName = `My Data ${formattedDate}`;

    const baseNameExists = pastExports.some(
      ({ name }) => name.toLowerCase() === newName.toLowerCase()
    );

    const existingNumbers = pastExports
      .map(({ name }) => name)
      .filter(exportName => {
        const baseNameMatch = exportName.toLowerCase().startsWith(newName.toLowerCase());
        if (!baseNameMatch) return false;
        return /\(\d+\)$/.test(exportName);
      })
      .map(exportName => {
        const match = /\((\d+)\)$/.exec(exportName);
        return match ? Number.parseInt(match[1], 10) : 0;
      })
      .filter(num => num > 0);

    let counter = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

    if (baseNameExists) {
      newName = `${newName} (${counter})`;
      counter++;
    }

    while (pastExports.some(({ name }) => name.toLowerCase() === newName.toLowerCase())) {
      newName = `${newName.split(' (')[0]} (${counter})`;
      counter++;
    }

    return newName;
  }, [pastExports, formatDateString]);

  const isNameInitialized = useEffectEvent(() => exportName !== null);

  useEffect(() => {
    if (isNameInitialized()) return;
    setExportName(createExportName());
  }, [createExportName]);

  // Derived Values
  const { fromValue, toValue } = getFromToValues(exportSettings);

  const isExcelPreview = useMemo(() => previewOptionId === EXCEL_EXPORT_TYPE, [previewOptionId]);

  const uniqueSymbols = useMemo(() => new Set(symbols), [symbols]);

  const exportOption = previewOptionId ? (optionById.get(previewOptionId) ?? null) : null;

  // API Request Strings (for display/copy)
  const apiKey = useLatestApiKey();
  const exportApiRequestString = useMemo(() => {
    const baseUrl = exportRequestService.buildApiRequestString({
      gxURL: GX_API_URL,
      symbols,
      fromValue,
      toValue: toValue || null,
      includeMetadata: exportSettings.includeMetadata,
      userTimezone
    });

    if (!apiKey) return baseUrl;

    try {
      const url = new URL(baseUrl);
      url.searchParams.set('token', apiKey);
      return url.toString();
    } catch {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}token=${apiKey}`;
    }
  }, [symbols, fromValue, toValue, exportSettings.includeMetadata, userTimezone, apiKey]);

  const exportCurlRequestString = useMemo(
    () =>
      exportRequestService.buildCurlRequestString({
        gxURL: GX_API_URL,
        symbols,
        fromValue,
        toValue: toValue || null,
        includeMetadata: exportSettings.includeMetadata,
        userTimezone,
        apiKey: apiKey ?? ''
      }),
    [symbols, fromValue, toValue, exportSettings.includeMetadata, userTimezone, apiKey]
  );

  // Posthog
  const capturePosthogEvent = useCallback(() => {
    if (exportOption?.buttonLabel === 'Enable access') {
      postHog.capture('datahub_enable_access_button_click', {
        button: exportOption.id
      });
      return;
    }

    const posthogData: Record<string, unknown> = {
      symbols,
      exportName: exportOption?.id,
      dateSettings: exportSettings.dateRangeType,
      includeMetadata: exportSettings.includeMetadata
    };

    if (exportSettings.dateRangeType === 'dates') {
      posthogData.from = exportSettings.from?.toLocaleString();
      posthogData.to = exportSettings.to?.toLocaleString();
    } else if (exportSettings.dateRangeType === 'range') {
      posthogData.period = exportSettings.period;
      posthogData.periodNumber = exportSettings.periodNumber;
    }

    postHog.capture('datahub_export_button_click', posthogData);
  }, [exportOption, postHog, symbols, exportSettings]);

  // Export Mutation
  const {
    mutateAsync: exportDataMutation,
    isPending: loading,
    isError
  } = useMutation({
    mutationFn: async (params: ExportRequestOverrides) => {
      const selectedOption = optionById.get(params.type);
      const exportSubType = params.exportSubType ?? selectedOption?.exportSubType ?? null;
      const isExcelExport = params.type === EXCEL_EXPORT_TYPE;
      const isRegeneration = Boolean(params.id);

      const requestBody = buildExportRequestBody({
        id: params.id,
        name: params.name ?? exportName,
        exportType: params.type,
        exportSubType,
        from: params.from ?? fromValue,
        to: params.to ?? toValue,
        timezone: params.timezone ?? userTimezone,
        dateFormat: params.dateFormat ?? userDateFormat,
        includeMetadata: params.includeMetadata ?? exportSettings.includeMetadata,
        symbols: params.symbols ?? symbols,
        group: params.group ?? DEFAULT_GROUP
      });

      if (isExcelExport) {
        setExcelExportStatus('loading');

        const response = await httpInstances.axiosGoInstance.post<ExportResponse>(
          EXPORT_ENDPOINT,
          requestBody,
          { validateStatus: status => status === 200 || status === 202 }
        );

        if (response.status === 200 && response.data.downloadUrl) {
          setExcelExportStatus('idle');
          const { fileId, fileName } = extractParamsFromUrl(response.data.downloadUrl);
          void downloadFile(fileId, fileName);
          showSuccessSnackbar(
            isRegeneration ? 'Export completed!' : 'Export completed! View in Past Exports anytime',
            'excel-export'
          );
          return response;
        } else if (response.status === 202) {
          setExcelExportStatus('queued');
          return response;
        }
        setExcelExportStatus('idle');
        showErrorSnackbar('Failed to export', 3000, 'excel-export');
      }

      const response = await httpInstances.axiosGoInstance.post<ExportResponse>(
        EXPORT_ENDPOINT,
        requestBody,
        { validateStatus: status => status === 200 || status === 202 }
      );

      // Handle download URL for regeneration (all export types)
      if (response.status === 200 && response.data.downloadUrl) {
        const { fileId, fileName } = extractParamsFromUrl(response.data.downloadUrl);
        void downloadFile(fileId, fileName);
        setExcelExportStatus('idle');
        showSuccessSnackbar(
          isRegeneration ? 'Export completed!' : 'Export completed! View in Past Exports anytime',
          'export'
        );
        return response;
      } else if (response.status === 202) {
        setExcelExportStatus('queued');
        return response;
      }

      setExcelExportStatus('idle');
      showSuccessSnackbar(
        isRegeneration ? 'Export completed!' : 'Export completed! View in Past Exports anytime',
        'export'
      );
      return response;
    },
    onMutate: async (params: ExportRequestOverrides) => {
      await queryClient.cancelQueries({ queryKey: pastExportsOptions.lists() });

      const previousExports = queryClient.getQueryData<ExportItemType[]>(
        pastExportsOptions.lists()
      );

      if (params.id) {
        // Regeneration: set status to ongoing and update timestamps
        queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
          old?.map(item =>
            item.id === params.id
              ? {
                  ...item,
                  exportStatus: 'ongoing',
                  savedAt: Date.now(),
                  lastExecutionTime: Date.now()
                }
              : item
          )
        );
      } else {
        // New export: add temporary item to list
        const tempId = `temp-${Date.now()}`;
        const selectedOption = optionById.get(params.type);
        const newExport: ExportItemType = {
          id: tempId,
          name: params.name ?? exportName ?? 'New Export',
          exportType: params.type,
          exportSubType: params.exportSubType ?? selectedOption?.exportSubType ?? null,
          savedAt: Date.now(),
          lastExecutionTime: Date.now(),
          exportStatus: 'ongoing',
          exportStatusDetails: null,
          exportSettings: {
            from: params.from ?? fromValue ?? '',
            to: params.to ?? toValue ?? null,
            timezone: params.timezone ?? userTimezone,
            dateFormat: params.dateFormat ?? userDateFormat,
            includeMetadata: params.includeMetadata ?? exportSettings.includeMetadata
          },
          symbols: (params.symbols ?? symbols).map(s => ({
            key: {
              code: s.Code,
              periodType: s.PeriodType,
              timeRef: s.TimeRef,
              period: s.Period
            },
            group: params.group ?? DEFAULT_GROUP
          }))
        };

        queryClient.setQueryData<ExportItemType[]>(pastExportsOptions.lists(), old =>
          old ? [newExport, ...old] : [newExport]
        );
      }

      return { previousExports };
    },
    onSuccess: () => {
      setExportName(null);
      setPreviewOptionId(null);

      // Sync with server after delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: pastExportsOptions.lists() });
      }, 5000);
    },
    onError: (_error, _params, context) => {
      // Rollback on error
      if (context?.previousExports) {
        queryClient.setQueryData(pastExportsOptions.lists(), context.previousExports);
      }
      setExcelExportStatus('idle');
      showErrorSnackbar(
        'Oops! Something went wrong. Please try again later.',
        3000,
        'export-error'
      );
    }
  });

  const resetExcelExportStatus = useCallback(() => {
    setExcelExportStatus('idle');
  }, []);

  // Handlers
  const handleCopyToClipBoard = useCallback(
    (textToCopy: string) => {
      capturePosthogEvent();
      copyToClipboard(textToCopy, 'Copied to clipboard!');
    },
    [capturePosthogEvent]
  );

  const handleExportData = useCallback(async () => {
    if (loading) return;
    capturePosthogEvent();
    if (previewOptionId) {
      return exportDataMutation({ type: previewOptionId });
    }
  }, [capturePosthogEvent, exportDataMutation, previewOptionId, loading]);

  const exportData = useCallback(
    async (type: string) => exportDataMutation({ type }),
    [exportDataMutation]
  );

  const scrambleCurlApiKey = useCallback((content: string) => scrambleSecrets(content), []);
  const scrambleApiToken = useCallback((content: string) => scrambleSecrets(content), []);

  const resetExportState = useCallback(() => {
    setExportSettings({
      ...defaultExportSettings,
      exportType:
        exportSliceType && exportSliceType === ExportTypes.DATA
          ? ExportTypes.DATA
          : ExportTypes.IMAGE
    });
    setPreviewOptionId(null);
    setShowSuccessScreen(false);
    setExportName(createExportName());
  }, [createExportName, exportSliceType]);

  return useMemo(
    () => ({
      // State
      exportSettings,
      setExportSettings,
      previewOptionId,
      setPreviewOptionId,
      showSuccessScreen,
      setShowSuccessScreen,
      exportName,
      setExportName,

      // Export functionality
      exportData,
      exportDataWithParams: exportDataMutation,
      timezone: userTimezone,
      loading,
      exportError: isError,
      isExcelPreview,

      // Excel export modal state
      excelExportStatus,
      setExcelExportStatus,
      resetExcelExportStatus,

      // Preview loading state
      previewLoading,
      setPreviewLoading,

      // Preview download function
      previewDownloadFn,
      setPreviewDownloadFn,

      // Preview content to copy (Python script, Power Query, curl, etc.)
      previewContentToCopy,
      setPreviewContentToCopy,

      // Close drawer with confirmation
      requestClose,
      setRequestClose,

      // Preview data
      exportOption,
      uniqueSymbols,
      exportApiRequestString,
      exportCurlRequestString,

      // Handlers
      handleCopyToClipBoard,
      handleExportData,
      capturePosthogEvent,
      scrambleCurlApiKey,
      scrambleApiToken,
      resetExportState
    }),
    [
      exportSettings,
      previewOptionId,
      showSuccessScreen,
      exportName,
      exportData,
      exportDataMutation,
      userTimezone,
      loading,
      isError,
      isExcelPreview,
      excelExportStatus,
      resetExcelExportStatus,
      previewLoading,
      previewDownloadFn,
      previewContentToCopy,
      setPreviewContentToCopy,
      requestClose,
      exportOption,
      uniqueSymbols,
      exportApiRequestString,
      exportCurlRequestString,
      handleCopyToClipBoard,
      handleExportData,
      capturePosthogEvent,
      scrambleCurlApiKey,
      scrambleApiToken,
      resetExportState
    ]
  );
};

export default useDataHubExport;
